import Locator from '../../../../../shared/Locator';
import type {
  AffectedProject,
  Category,
  Issue,
  Scope,
} from '../../../../../shared/issuesV2';
import {
  IssueScopeKind,
} from '../../../../../shared/issuesV2';
import { getProjectBaseURLFromParams, getProjectIssuesFullPageURLFromParams } from '../../../../utils';

type GetIssueDetailURLForScope = {
  affectedProject: AffectedProject;
  scope: Scope;
  issueDetailsURL: string;
  issueCategory?: Category;
  issueDetails?: Issue;
};

/**
 * Returns a string URL pointing to one of:
 *  - Fullscreen Issue Details page in selected project's default branch (Global/Project Scope)
 *  - Fullscreen Issue Details page in selected project's current branch (Project Scope)
 *  - Release Group revision homepage (Release Group Scope)
 *
 * Depending on the current scope and the clicked affected project.
 * @returns string
 */
export const getIssueDetailURLForScope = ({
  affectedProject,
  scope,
  issueDetailsURL,
  issueCategory,
  issueDetails,
}: GetIssueDetailURLForScope) => {
  if (scope.type === IssueScopeKind.ReleaseGroup) {
    // When viewing an issue from a Release Group, redirect to
    //  the specific project revision that's in this release of the RG
    return getProjectBaseURLFromParams({
      project_id: affectedProject.id,
      reference_type: 'branch',
      reference: affectedProject.branch,
      revision_id: Locator.parse(affectedProject.revisionId).revision ?? '',
    });
  }

  if (!issueCategory || !issueDetails?.id) {
    // fallback to a default project base url if
    //  a specific issue URL cannnot be found
    return getProjectBaseURLFromParams({
      project_id: affectedProject.id,
      reference_type: 'branch',
      reference: affectedProject.branch,
      revision_id: Locator.parse(affectedProject.revisionId).revision ?? '',
    });
  }

  if (scope.type === IssueScopeKind.Project && scope.id === affectedProject.id && issueDetailsURL) {
    // when in Project scope, clicking on the currently active project redirects to
    // the project issue details on the current branch. Mirrors the `full-screen` button click
    // Only return the issueDetailsURL if it's provided, otherwise fallback to the default project base url
    return issueDetailsURL;
  }

  // for all other Project and Global scope issue access, redirect to
  //  the project issue details on the default branch
  return getProjectIssuesFullPageURLFromParams({
    projectId: affectedProject.id,
    referenceType: 'branch',
    reference: affectedProject.branch,
    revisionId: Locator.parse(affectedProject.revisionId).revision ?? '',
    issueCategory,
    issueId: issueDetails.id.toString(),
  });
};
